import { useEffect } from "react"
import api from "../lib/api"

const HEARTBEAT_INTERVAL_MS = 15000

export type ProgressHeartbeatResult = {
  completed: boolean
  watch_time_seconds: number
  last_position_seconds: number
  max_scroll_percent: number | null
}

type UseProgressHeartbeatArgs = {
  lessonId: string | null | undefined
  enabled: boolean
  /**
   * Returns true only if there was genuine activity (playback progressed, or
   * the reader is visibly scrolling/present) since the last tick. When it
   * returns false the tick is skipped entirely — no delta is sent, so idle
   * time never accumulates as "watched" time.
   */
  isActiveSinceLastTick: () => boolean
  getPositionSeconds?: () => number | undefined
  getScrollPercent?: () => number | undefined
  /**
   * Duración real de YouTube ya conocida en este montaje, si la hay. Se
   * reenvía en cada heartbeat — el backend la persiste write-once, así que
   * reenviarla tras la primera vez es un no-op inofensivo. Ver
   * openspec/changes/academy-lesson-watch-time-completion.
   */
  getYoutubeDurationSeconds?: () => number | undefined
  onProgress?: (result: ProgressHeartbeatResult) => void
}

/**
 * Reports real elapsed time to the backend every ~15s while the lesson is
 * open, the tab is visible, and there was genuine activity in the interval.
 * Page Visibility API pauses reporting when the tab is hidden — see
 * openspec/changes/academy-lesson-view-validation/design.md (D4).
 */
export function useProgressHeartbeat({
  lessonId,
  enabled,
  isActiveSinceLastTick,
  getPositionSeconds,
  getScrollPercent,
  getYoutubeDurationSeconds,
  onProgress,
}: UseProgressHeartbeatArgs) {
  useEffect(() => {
    if (!enabled || !lessonId) return

    const tick = () => {
      if (document.hidden) return
      if (!isActiveSinceLastTick()) return

      const payload: Record<string, number> = { delta_seconds: HEARTBEAT_INTERVAL_MS / 1000 }
      const position = getPositionSeconds?.()
      if (position !== undefined) payload.position_seconds = position
      const scroll = getScrollPercent?.()
      if (scroll !== undefined) payload.scroll_percent = scroll
      const youtubeDuration = getYoutubeDurationSeconds?.()
      if (youtubeDuration !== undefined) payload.youtube_duration_seconds = youtubeDuration

      api.put(`/academy/lessons/${lessonId}/progress`, payload)
        .then(({ data }) => {
          if (data?.data) onProgress?.(data.data as ProgressHeartbeatResult)
        })
        .catch(() => {})
    }

    const interval = setInterval(tick, HEARTBEAT_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [lessonId, enabled, isActiveSinceLastTick, getPositionSeconds, getScrollPercent, getYoutubeDurationSeconds, onProgress])
}
